import { PRAYERS, type PrayerId } from './prayer.js';
import { minutes, sinceFajr, type PrayerTimeSet, type TimeOfDay } from './times.js';

/** When the prayer stands: some minutes after the call, or at a set time. */
export type Iqama = { kind: 'after'; minutes: number } | { kind: 'at'; time: TimeOfDay };

export interface PrayerWindowConfig {
  iqama: Iqama;
  /** How long the prayer takes, in minutes, from iqama. */
  lengthMinutes: number;
}

export interface WindowConfig {
  prayers: Record<PrayerId, PrayerWindowConfig>;
  /** Minutes before iqama a person starts getting ready. */
  getReadyMinutes: number;
  /** Minutes each way to the masjid. */
  travelMinutes: number;
}

/**
 * Where a person prays: `home` is where they already are (no travel),
 * `masjid` grows the window by the travel each way, and `there` is praying
 * where they are today (practice, school, work), which drops the travel.
 */
export type Where = 'home' | 'masjid' | 'there';

export const DEFAULT_WINDOW_CONFIG: WindowConfig = {
  prayers: Object.fromEntries(
    PRAYERS.map((id) => [id, { iqama: { kind: 'after', minutes: 15 }, lengthMinutes: 10 }]),
  ) as Record<PrayerId, PrayerWindowConfig>,
  getReadyMinutes: 5,
  travelMinutes: 0,
};

/**
 * A prayer's window, in minutes on the day's own clock: 0 is midnight at the
 * start of the day's date, and a prayer past midnight (a far-north ‘Ishā')
 * runs on past 1440 rather than wrapping back to the morning.
 */
export interface BusyWindow {
  /** The person's busy time begins: travel, then getting ready. */
  start: number;
  /** The call to prayer. */
  call: number;
  iqama: number;
  /** The prayer is over (iqama + length), before any travel home. */
  prayerEnd: number;
  /** The person is free again, after travel. */
  end: number;
}

function travelFor(config: WindowConfig, where: Where): number {
  return where === 'masjid' ? Math.max(0, config.travelMinutes) : 0;
}

export function busyWindow(
  day: PrayerTimeSet,
  prayer: PrayerId,
  config: WindowConfig,
  where: Where,
): BusyWindow {
  // Put the call on the prayer-day's own scale, so one past midnight is later.
  const call = minutes(day.fajr) + sinceFajr(day[prayer], day.fajr);
  const rule = config.prayers[prayer];

  let iqama: number;
  if (rule.iqama.kind === 'after') {
    iqama = call + Math.max(0, rule.iqama.minutes);
  } else {
    let at = minutes(rule.iqama.time);
    // A set time reads on the same scale as the call, and can never be before it.
    while (at < call - 720) at += 1440;
    while (at > call + 720) at -= 1440;
    iqama = Math.max(call, at);
  }

  const prayerEnd = iqama + Math.max(0, rule.lengthMinutes);
  const travel = travelFor(config, where);
  return {
    start: iqama - Math.max(0, config.getReadyMinutes) - travel,
    call,
    iqama,
    prayerEnd,
    end: prayerEnd + travel,
  };
}

/**
 * The moment an anchored thing is set to: "N min before a prayer" counts from
 * the call, "N min after" from when the window ends (iqama + length, plus
 * travel for whoever went to the masjid). Minutes on the day's own clock, as
 * in `BusyWindow`; `toTimeOfDay` turns it into 'HH:MM'.
 */
export function anchorTime(
  day: PrayerTimeSet,
  prayer: PrayerId,
  side: 'before' | 'after',
  offsetMinutes: number,
  config: WindowConfig,
  where: Where,
): number {
  const w = busyWindow(day, prayer, config, where);
  return side === 'before' ? w.call - offsetMinutes : w.end + offsetMinutes;
}
